import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from "react";
import { Box, Card, Typography, Pagination, keyframes, Accordion, AccordionSummary, AccordionDetails, } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { DropDownArrowIcon } from "@ippon/shared-assets";
import { CustomTruncateText, CustomCheckbox } from "../../index";
import { colors } from "../../theme/colors";
// Loading spinner animation
const spinAnimation = keyframes `
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;
// Custom Loading Overlay
const CustomLoadingOverlay = () => (_jsx(Box, { sx: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        zIndex: 10,
        minHeight: "300px",
    }, children: _jsx(Box, { sx: {
            display: "flex",
            alignItems: "center",
            gap: "12px",
        }, children: _jsx(Box, { sx: {
                width: "50px",
                height: "50px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #0B9DBD",
                borderRadius: "50%",
                animation: `${spinAnimation} 1s linear infinite`,
            } }) }) }));
const MobileTable = ({ rows, columns, onRowSelection, selectedIds = [], isLoading = false, pagination, checkBoxOpen = true, onPageChange, handlePropertyClick, // Accept the click handler
disableNameClick = false, // Accept the disable click prop
hidePagination = false, // Accept the hide pagination prop
pageSize: pageSizeProp, // Accept the pageSize prop
getRowSx, renderTitle, noDataMessage = "データが登録されていません", noDataIcon, }) => {
    const [localPage, setLocalPage] = useState(1);
    // Calculate the maximum label width dynamically
    const getMaxLabelWidth = () => {
        const visibleColumns = columns.filter((col) => col.field !== "name" &&
            col.field !== "id" &&
            col.field !== "report_title");
        const labelLengths = visibleColumns.map((column) => {
            const label = column?.headerName || column.field;
            return label.length;
        });
        const maxLength = Math.max(...labelLengths);
        // Estimate width based on character count for 14px font (more conservative calculation)
        // Use 35% of screen width as maximum to prevent overflow
        const calculatedWidth = Math.max(100, maxLength * 8 + 16); // minimum 100px, 8px per char + padding
        const maxAllowedWidth = Math.min(calculatedWidth, window.innerWidth * 0.35);
        return maxAllowedWidth;
    };
    const maxLabelWidth = getMaxLabelWidth();
    const handleSelectionChange = (rowId, checked) => {
        if (!onRowSelection)
            return;
        let newSelectedIds;
        if (checked) {
            newSelectedIds = [...(selectedIds || []), rowId];
        }
        else {
            newSelectedIds = (selectedIds || []).filter((id) => id !== rowId);
        }
        onRowSelection(newSelectedIds);
    };
    const handlePageChange = (_event, value) => {
        if (pagination && onPageChange) {
            onPageChange(value);
        }
        else {
            setLocalPage(value);
        }
    };
    // Calculate pagination values
    const getPaginationValues = () => {
        if (pagination) {
            return {
                totalPages: Math.ceil(pagination.total / pagination.limit),
                currentPage: pagination.page,
                showPagination: pagination.total > pagination.limit,
            };
        }
        else {
            const pageSize = pageSizeProp ?? 10;
            return {
                totalPages: Math.ceil(rows.length / pageSize),
                currentPage: localPage,
                showPagination: rows.length > pageSize,
            };
        }
    };
    const { totalPages, currentPage, showPagination } = getPaginationValues();
    // Calculate displayed rows for pagination
    const displayedRows = React.useMemo(() => {
        if (pagination || hidePagination) {
            return rows;
        }
        const pageSize = pageSizeProp ?? 10;
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        return rows.slice(start, end);
    }, [rows, pagination, hidePagination, currentPage, pageSizeProp]);
    // Get field mappings from columns
    const getFieldLabel = (field) => {
        const column = columns.find((col) => col.field === field);
        return column?.headerName || field;
    };
    // Render field value based on column configuration
    const renderFieldValue = (row, field) => {
        const column = columns.find((col) => col.field === field);
        if (column?.renderCell) {
            return column.renderCell({ row, value: row[field] });
        }
        return row[field];
    };
    // Handle name click for navigation
    const handleNameClick = (row, event) => {
        event.stopPropagation(); // Prevent accordion toggle
        if (handlePropertyClick) {
            handlePropertyClick(row);
        }
    };
    return (_jsxs(Box, { sx: { position: "relative" }, children: [isLoading && _jsx(CustomLoadingOverlay, {}), !isLoading && displayedRows.length === 0 && (_jsxs(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 6,
                    minHeight: "160px",
                }, children: [noDataIcon ?? (_jsx(InboxOutlinedIcon, { sx: { fontSize: 40, color: colors.border.light, mb: 1 } })), _jsx(Typography, { sx: {
                            fontSize: "14px",
                            color: colors.text.secondary,
                            textAlign: "center",
                        }, children: noDataMessage })] })), displayedRows.map((row, index) => (_jsx(Card, { sx: {
                    marginBottom: 1,
                    borderRadius: "8px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    width: "100%",
                    overflow: "hidden",
                    boxSizing: "border-box",
                }, children: _jsxs(Accordion, { sx: {
                        boxShadow: "none",
                        "&:before": { display: "none" },
                        "&.MuiAccordion-root": { margin: 0 },
                        width: "100%",
                        overflow: "hidden",
                    }, defaultExpanded: false, children: [_jsx(AccordionSummary, { expandIcon: _jsx(DropDownArrowIcon, { sx: { width: "14px", height: "14px", flexShrink: 0 } }), sx: {
                                padding: "8px 16px",
                                minHeight: "40px !important",
                                height: "40px !important",
                                width: "100%",
                                overflow: "hidden",
                                "&.Mui-expanded": {
                                    backgroundColor: "#F0F8FF",
                                },
                                backgroundColor: selectedIds?.includes(row.id)
                                    ? "#F0F8FF"
                                    : "transparent",
                                ...(getRowSx ? getRowSx(row) : {}),
                                "& .MuiAccordionSummary-content": {
                                    alignItems: "center",
                                    margin: "0px !important",
                                    overflow: "hidden",
                                    width: "100%",
                                },
                                "& .MuiAccordionSummary-content.Mui-expanded": {
                                    margin: "0px !important",
                                },
                                "& .MuiAccordionSummary-expandIconWrapper": {
                                    flexShrink: 0,
                                    marginLeft: "8px",
                                },
                                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                                    transform: "rotate(180deg)",
                                },
                            }, children: _jsxs(Box, { sx: {
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    overflow: "hidden",
                                }, children: [checkBoxOpen && (_jsx(Box, { onClick: (e) => {
                                            e.stopPropagation();
                                        }, sx: {
                                            marginRight: 1,
                                            flexShrink: 0,
                                        }, children: _jsx(CustomCheckbox, { checked: selectedIds?.includes(row.id) || false, onChange: () => {
                                                const isChecked = selectedIds?.includes(row.id) || false;
                                                handleSelectionChange(row.id, !isChecked);
                                            }, size: "small" }) })), _jsx(Box, { sx: {
                                            flex: 1,
                                            minWidth: 0,
                                            maxWidth: checkBoxOpen
                                                ? "calc(100% - 50px)"
                                                : "calc(100% - 20px)", // より多くの文字を表示できるように調整
                                            overflow: "hidden",
                                            paddingRight: 1, // アイコンとの間により多くのスペースを確保
                                        }, children: renderTitle ? (renderTitle(row)) : (_jsx(Typography, { variant: "body1", onClick: disableNameClick
                                                ? undefined
                                                : (e) => handleNameClick(row, e), sx: {
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                textDecoration: disableNameClick ? "none" : "underline",
                                                cursor: disableNameClick ? "default" : "pointer",
                                                color: "#3e3e3e",
                                                display: "block",
                                                width: "100%",
                                            }, children: _jsx(CustomTruncateText, { text: row.title ||
                                                    row.propertyName ||
                                                    row.customerName ||
                                                    row.customer_name ||
                                                    row.name ||
                                                    (row.firstName && row.lastName
                                                        ? `${row.lastName} ${row.firstName}`
                                                        : "") ||
                                                    String(row.id || "") }) })) })] }) }), _jsx(AccordionDetails, { sx: {
                                padding: "0px 16px 2px 16px",
                                width: "100%",
                                overflow: "hidden",
                                boxSizing: "border-box",
                            }, children: columns
                                .filter((col) => col.field !== "name" &&
                                col.field !== "id" &&
                                col.field !== "report_title")
                                .map((column, fieldIndex) => (_jsxs(Box, { sx: {
                                    display: "flex",
                                    paddingY: "5px",
                                    borderBottom: fieldIndex < columns.length - 2
                                        ? "1px solid #f0f0f0"
                                        : "none",
                                    width: "100%",
                                    overflow: "hidden",
                                    boxSizing: "border-box",
                                }, children: [_jsx(Typography, { variant: "body2", sx: {
                                            minWidth: `${maxLabelWidth}px`,
                                            maxWidth: `${maxLabelWidth}px`,
                                            width: `${maxLabelWidth}px`,
                                            fontWeight: 500,
                                            color: "#666",
                                            backgroundColor: "#f9f9f9",
                                            padding: "4px 8px",
                                            borderRadius: "8px",
                                            marginRight: 2,
                                            flexShrink: 0,
                                            fontSize: "14px",
                                            lineHeight: 1.2,
                                            display: "flex",
                                            alignItems: "center",
                                            whiteSpace: "normal",
                                            wordWrap: "break-word",
                                            overflowWrap: "break-word",
                                            wordBreak: "break-all",
                                        }, children: getFieldLabel(column.field) }), _jsx(Box, { sx: {
                                            flex: 1,
                                            paddingY: "4px",
                                            minWidth: 0,
                                            // 全てのフィールドでoverflow: visibleに
                                            overflow: "visible",
                                        }, children: _jsx(Typography, { component: "div", variant: "body2", sx: {
                                                fontSize: "14px",
                                                lineHeight: 1.2,
                                                // 全てのフィールドで折り返し表示
                                                whiteSpace: "normal",
                                                textOverflow: "clip",
                                                overflow: "visible",
                                                wordWrap: "break-word",
                                                overflowWrap: "break-word",
                                                wordBreak: "break-all",
                                                "& > *": {
                                                    maxWidth: "100%",
                                                    fontSize: "14px",
                                                },
                                                "& .MuiFormControl-root": {
                                                    maxWidth: "100%",
                                                    minWidth: "120px",
                                                    width: "100%",
                                                },
                                                "& .MuiSelect-select": {
                                                    fontSize: "14px",
                                                    padding: "4px 8px",
                                                    minHeight: "20px",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    minHeight: "32px",
                                                },
                                            }, children: renderFieldValue(row, column.field) }) })] }, column.field))) })] }) }, row.id || index))), !hidePagination && showPagination && totalPages > 1 && (_jsx(Box, { display: "flex", justifyContent: "center", alignItems: "center", mt: 3, children: _jsx(Pagination, { shape: "rounded", count: totalPages, page: currentPage, onChange: handlePageChange, disabled: isLoading, size: "small", sx: {
                        "& .MuiPaginationItem-root": {
                            backgroundColor: "#F5F5F5",
                            color: "#000",
                            borderRadius: "6px",
                            "&.Mui-selected": {
                                backgroundColor: "#039dbe",
                                color: "#fff",
                                "&:hover": {
                                    backgroundColor: "#039dbe",
                                },
                            },
                            "&:hover": {
                                backgroundColor: "#DBF8FF",
                            },
                            "&.Mui-disabled": {
                                backgroundColor: "#F5F5F5",
                            },
                        },
                    } }) }))] }));
};
export default MobileTable;
